// Merchant-facing rate card — READ ONLY.
//
// The merchant portal already lists which couriers are assigned, but not what any of them costs, so
// a merchant had to ask us before they could work out their own margin. This shows the same plans
// admin sees, rendered as tables rather than as an editable form: rates are negotiated with us, so
// nothing here is a control the merchant can move.
//
// It reads `GET /merchants/:id/pricing`, which is now pinned to the caller's own merchant id
// server-side (see `assertOwnMerchant`) — the id in the URL cannot be swapped for a competitor's.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Truck, Info } from 'lucide-react';
import { api } from '@/api/client';

interface PricingPlan {
  id: number;
  serviceType: 'B2C' | 'B2B';
  courierIds: number[];
  courierNames?: string[];
  fuelChargePercent?: number;
  config: any;
  isActive: boolean;
  updatedAt: string;
}

/** The B2C zone columns, in the order the rate card is negotiated in. */
const B2C_ZONES: { key: string; label: string; hint: string }[] = [
  { key: 'city', label: 'City', hint: 'Within the same city' },
  { key: 'state', label: 'State', hint: 'Within the same state' },
  { key: 'metro', label: 'Metro', hint: 'Between metro cities' },
  { key: 'zone', label: 'Zone', hint: 'Within the same region' },
  { key: 'roi', label: 'ROI', hint: 'Rest of India' },
  { key: 'sd', label: 'SD', hint: 'Special destinations (J&K, NE, islands)' },
];

const B2B_OTHER_LABELS: Record<string, string> = {
  min_lr_charge: 'Minimum LR charge',
  fuel_charge_percent: 'Fuel surcharge',
  processing_charge: 'Processing charge',
  rov_owner: 'ROV — owner risk',
  rov_carrier: 'ROV — carrier risk',
  oda_charge_amt: 'ODA charge',
  fm_charges: 'First-mile pickup',
  pod_charge: 'POD charge',
  green_tax_amt: 'Green tax',
  cod_charge_amt: 'COD charge',
  to_pay_handling_charges: 'To-pay handling',
  cheque_charge_amt: 'Cheque charge',
  demurrage_charge_amt: 'Demurrage',
  other_handling_charge_amt: 'Other handling',
  divisor: 'Volumetric divisor',
  min_charge_weight: 'Minimum chargeable weight',
};

export function MerchantRatesTab({ merchantId }: { merchantId: number }) {
  const [filter, setFilter] = useState<'ALL' | 'B2C' | 'B2B'>('ALL');

  const { data, isLoading, error } = useQuery<PricingPlan[]>({
    queryKey: ['merchant-rates', merchantId],
    queryFn: async () => (await api.get<PricingPlan[]>(`/merchants/${merchantId}/pricing`)).data ?? [],
  });

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
        {(error as any)?.response?.data?.message ?? 'Could not load your rates'}
      </div>
    );
  }

  const plans = (data ?? []).filter((p) => p.isActive && (filter === 'ALL' || p.serviceType === filter));

  if ((data ?? []).length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-600">
        No rate card has been set up for your account yet.
        <div className="text-xs text-slate-500 mt-1">Contact your account manager and it will appear here.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
        <Info size={15} className="shrink-0 mt-0.5 text-slate-400" />
        <div>
          <div className="text-slate-700 font-medium">Your negotiated rates</div>
          <div className="text-slate-500 text-xs mt-0.5">
            These are the rates your shipments are billed at, per courier. They&apos;re agreed with us and
            shown here for reference only — contact your account manager to discuss a change. Freight is
            calculated on the higher of actual and volumetric weight, and GST is added on top.
          </div>
        </div>
      </div>

      <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm w-fit">
        {(['ALL', 'B2C', 'B2B'] as const).map((t) => (
          <button key={t} onClick={() => setFilter(t)}
            className={`px-3 py-1.5 ${filter === t ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            {t}
          </button>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-sm text-slate-400 py-6 text-center">No active {filter} rate card.</div>
      )}

      {plans.map((p) => (
        <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex flex-wrap items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${p.serviceType === 'B2B' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {p.serviceType}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
              <Truck size={14} className="text-slate-400" />
              {p.courierNames?.length ? p.courierNames.join(', ') : 'All couriers'}
            </span>
            {p.fuelChargePercent != null && Number(p.fuelChargePercent) > 0 && (
              <span className="text-xs text-slate-500 ml-auto">Fuel surcharge {Number(p.fuelChargePercent)}%</span>
            )}
          </div>
          <div className="p-4">
            {p.serviceType === 'B2C' ? <B2cRates config={p.config} /> : <B2bRates config={p.config} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function B2cRates({ config }: { config: any }) {
  const cfg = config ?? {};
  // `sameAsAbove` / `sameAsForward` mean the slab reuses the one above it — admin stores the copied
  // values too, so nothing has to be resolved here; the flag is only shown as a note.
  const rows: { title: string; row: any; note?: string }[] = [
    { title: 'Forward — first slab', row: cfg.forwardBasic },
    { title: 'Forward — each additional', row: cfg.forwardAdditional, note: cfg.forwardAdditional?.sameAsAbove ? 'same as first slab' : undefined },
    { title: 'RTO — first slab', row: cfg.rtoBasic, note: cfg.rtoBasic?.sameAsForward ? 'same as forward' : undefined },
    { title: 'RTO — each additional', row: cfg.rtoAdditional, note: cfg.rtoAdditional?.sameAsForward ? 'same as forward' : cfg.rtoAdditional?.sameAsAbove ? 'same as first slab' : undefined },
  ].filter((r) => r.row && Object.keys(r.row).length > 0);

  if (!rows.length) return <div className="text-sm text-slate-400">No slab rates configured.</div>;

  const cod = cfg.forwardBasic ?? {};

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left py-1.5 pr-3 whitespace-nowrap">Slab</th>
              <th className="text-right py-1.5 px-2 whitespace-nowrap">Upto (kg)</th>
              {B2C_ZONES.map((z) => (
                <th key={z.key} className="text-right py-1.5 px-2 whitespace-nowrap" title={z.hint}>{z.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.title} className="border-t border-slate-100">
                <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">
                  {r.title}
                  {r.note && <span className="text-[11px] text-slate-400 ml-1.5">({r.note})</span>}
                </td>
                <td className="py-2 px-2 text-right font-mono text-xs">{r.row.upto ?? '—'}</td>
                {B2C_ZONES.map((z) => (
                  <td key={z.key} className="py-2 px-2 text-right font-mono text-xs">{money(r.row[z.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600 border-t border-slate-100 pt-3">
        {cod.codAmount != null && cod.codAmount !== '' && <span>COD charge: <b>₹{Number(cod.codAmount)}</b></span>}
        {cod.codPercent != null && cod.codPercent !== '' && <span>COD percent: <b>{Number(cod.codPercent)}%</b></span>}
        <span className="text-slate-400">Zones: {B2C_ZONES.map((z) => `${z.label} = ${z.hint}`).join(' · ')}</span>
      </div>
    </div>
  );
}

function B2bRates({ config }: { config: any }) {
  const cfg = config ?? {};
  const matrix = cfg.zoneMatrix ?? {};
  const origins = Object.keys(matrix);
  // Destination columns come from the rows themselves — the matrix is not guaranteed square, and
  // hard-coding a zone list would silently drop any zone we later add.
  const dests = Array.from(new Set(origins.flatMap((o) => Object.keys(matrix[o] ?? {}))));
  const other = cfg.otherCharges ?? {};

  return (
    <div className="space-y-4">
      {origins.length > 0 ? (
        <div className="overflow-x-auto">
          <div className="text-xs text-slate-500 mb-1.5">Per-kg rate by origin → destination zone (₹)</div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left py-1.5 pr-3">From \ To</th>
                {dests.map((d) => <th key={d} className="text-right py-1.5 px-2 whitespace-nowrap">{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {origins.map((o) => (
                <tr key={o} className="border-t border-slate-100">
                  <td className="py-2 pr-3 font-medium text-slate-700">{o}</td>
                  {dests.map((d) => (
                    <td key={d} className="py-2 px-2 text-right font-mono text-xs">{money(matrix[o]?.[d])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-sm text-slate-400">No zone rates configured.</div>
      )}

      {Object.keys(other).length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <div className="text-xs text-slate-500 mb-2">Other charges</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
            {Object.entries(other).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3">
                <span className="text-slate-500">{B2B_OTHER_LABELS[k] ?? k.replace(/_/g, ' ')}</span>
                <span className="text-slate-800 font-mono">{chargeValue(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function money(v: any) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n}` : String(v);
}

/**
 * A B2B other-charge is either a plain number or a `{ min, unit }` pair (e.g. ROV is 0.2% of invoice
 * value with a ₹100 floor). Rendering the object raw printed "[object Object]".
 */
function chargeValue(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') {
    const min = (v as any).min;
    const unit = (v as any).unit;
    const parts: string[] = [];
    if (unit !== undefined && unit !== null && unit !== '') parts.push(String(unit));
    if (min !== undefined && min !== null && min !== '') parts.push(`min ${min}`);
    return parts.length ? parts.join(' · ') : '—';
  }
  return String(v);
}
